<template>
    <div>
        <div v-if="viewState == 'Idle'">
            <div class="card mb-4" >
                <div class="card-body">
                    <div class="row">
                        <div class="col">
                            <h4 class="card-title">
                                <span class="mdi mdi-message-text-outline"></span> 
                                Standard Phrases
                                <span v-if="isSearchResult"> - {{ phrases.length }} results for '{{ searchTermWas }}'</span>
                            </h4>
                        </div>
                        <div class="col">
                            <div class="form-inline float-right">
                                <div class="input-group input-group mr-2">
                                    <input type="text" class="form-control form-control-sm" placeholder="Search phrases by name, code or content" aria-label="Search Phrases" v-model="searchTerm" @keydown.enter="searchPhrases" style="width: 300px;">
                                    <div class="input-group-append">
                                        <button class="btn btn-primary btn-sm" type="button" @click="searchPhrases">
                                            <span class="mdi mdi-magnify"></span>
                                        </button>
                                    </div>
                                </div>

                                <button class="btn btn-primary btn-sm float-right" @click="addPhrase">
                                    <span class="mdi mdi-plus"></span> New Phrase
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="row mt-2">
                        <div class="col">
                            <div v-if="!isSearchResult">
                                <button class="btn btn-primary btn-sm mr-2" :disabled="pageNumber == 1" @click="previousPage">
                                    &laquo; Previous Page
                                </button>
                                <button class="btn btn-primary btn-sm" :disabled="pageNumber == totalPages" @click="nextPage">
                                    Next Page &raquo;
                                </button>
                            </div>

                            <div v-if="isSearchResult">
                                <button class="btn btn-primary btn-sm mr-2" @click="loadPhrases(resultPageSize, resultPageNumber)">
                                    <span class="mdi mdi-close"></span> Clear Search Results
                                </button>
                            </div>
                        </div>
                        <div class="col">
                            <div class="float-right" v-if="!isSearchResult">
                                Page <strong>{{ pageNumber }}</strong> of <strong>{{ totalPages }}</strong> |
                                <strong>{{ totalResults }}</strong> results
                            </div>                            
                        </div>
                    </div>
                </div>
            </div>

            <h4 class="text-muted text-center mt-5" v-if="isSearchResult && (!phrases || phrases.length == 0)">
                No phrases found for search '{{ searchTermWas }}'
            </h4>

            <div class="card mb-2" v-for="(phrase) in phrases" :key="phrase.phrase_id">
                <div class="card-body">
                    <div class="form-group">
                        <label>Name</label>
                        <input type="text" class="form-control form-control-sm" v-model="phrase.name">
                    </div>

                    <div class="form-group">
                        <label>Lookup Code</label>
                        <input type="text" class="form-control form-control-sm" v-model="phrase.code">
                    </div>

                    <div class="form-group">
                        <label>Applies to Categories</label>
                        <input type="text" class="form-control form-control-sm" v-model="phrase.categories_str">
                    </div>

                    <div class="form-group">
                        <label>Content</label>
                        <textarea class="form-control" rows="4" style="resize: none;" v-model="phrase.content"></textarea>
                    </div>
                </div>
                <div class="card-footer">
                    <button class="btn btn-primary btn-sm mr-2" @click="savePhraseChanges(phrase)">
                        <span class="mdi mdi-content-save-all-outline"></span> Save Changes
                    </button>

                    <button class="btn btn-danger btn-sm" @click="confirmDeletePhrase(phrase)">
                        <span class="mdi mdi-trash-can-outline"></span> Delete
                    </button>
                </div>
            </div>
        </div>

        <b-modal id="modal-add-phrase" 
            title="New Phrase"
            ok-title="Save Phrase"        
            :ok-disabled="!creatingPhrase.content"
            @ok="onAddPhraseOK">

            <div class="form-group">
                <label>Lookup Code</label>
                <input type="text" class="form-control form-control-sm" ref="tbPhraseName" v-model="creatingPhrase.code" placeholder="Lookup code for this phrase">
            </div>

            <div class="form-group">
                <label>Name</label>
                <input type="text" class="form-control form-control-sm" v-model="creatingPhrase.name" placeholder="Phrase name">
            </div>

            <div class="form-group">
                <label>Applies to Categories</label>
                <input type="text" class="form-control form-control-sm" v-model="creatingPhrase.categories_str" placeholder="Comma seperated sections, e.g. 1.2,1.3">
            </div>

            <div class="form-group">
                <label>Content</label>
                <textarea class="form-control" rows="4" style="resize: none;" v-model="creatingPhrase.content"></textarea>
            </div>
        </b-modal>
        
        <LoadingIndicator v-if="viewState == 'Busy'" />
        <ErrorMessage v-if="viewState == 'Error'" />
    </div>
</template>

<script>
    import axios from 'axios'

    export default {
        props: {
            resultPageSize: {
                type: Number, 
                default: 10
            },
            resultPageNumber: {
                type: Number, 
                default: 1
            }
        },
        data() {
            return {
                viewState: 'Idle',
                error: null,
                pageNumber: null,
                totalPages: null,
                pageSize: null,
                totalResults: null,
                isSearchResult: false,
                searchSummary: null,
                searchTerm: null,
                searchTermWas: null,
                phrases: null,
                creatingPhrase: { }
            }
        },
        async mounted() {
            await this.loadPhrases(this.resultPageSize, this.resultPageNumber)
        },
        methods: {
             nextPage() {
                this.loadPhrases(this.resultPageSize, this.pageNumber + 1)
            },
            previousPage() {
                this.loadPhrases(this.resultPageSize, this.pageNumber - 1)
            },
            addPhrase() {
                this.creatingPhrase = { }
                this.$bvModal.show('modal-add-phrase')
            },
            async onAddPhraseOK(bvEvt) {
                bvEvt.preventDefault()

                try {
                    this.creatingPhrase.categories = this.creatingPhrase.categories_str ? 
                        this.creatingPhrase.categories_str.split(',').map((c) => { return c.trim() }) : []

                    let createResponse = await axios.post(`/api/standardphrases`, this.creatingPhrase)                    
                    this.$bvModal.hide('modal-add-phrase')
                    await this.loadPhrases(this.pageSize, this.pageNumber)

                     this.$bvToast.toast('New Phrase Saved', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t save the phrase', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }
            },
            async savePhraseChanges(phrase) {
                try {
                    let categories = phrase.categories_str ? 
                        phrase.categories_str.split(',').map((c) => { return c.trim() }) : []
                    
                    let updateUrl = `/api/standardphrases/${phrase.phrase_id}`
                    let updateResponse = await axios.put(updateUrl, {
                        content: phrase.content, 
                        name: phrase.name,
                        code: phrase.code,
                        categories: categories
                    })

                    this.$bvToast.toast('Phrase updated', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t save the phrase', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }
            },
            confirmDeletePhrase(phrase) {
                this.$bvModal
                    .msgBoxConfirm('Are you sure you wish to delete this phrase?')
                    .then(async (confirm) => {
                        if (!confirm)
                            return

                        try {
                            let updateResponse = await axios.delete(`/api/standardphrases/${phrase.phrase_id}`)
                            await this.loadPhrases(this.pageSize, this.pageNumber)

                            this.$bvToast.toast('Phrase deleted', {
                                title: 'Success',
                                variant: 'success',
                                solid: true,
                                appendToast: true,
                                autoHideDelay: 3000
                            })
                        } catch (err) {
                            console.error(err)
                            this.$bvToast.toast('Sorry, something went wrong and we couldn\'t save the phrase', {
                                title: 'Error',
                                variant: 'danger',
                                solid: true,
                                appendToast: true,
                                autoHideDelay: 3000
                            })
                        }
                    })
            },
            async loadPhrases(pageSize, pageNumber) {
                this.viewState = 'Busy'
                this.searchTermWas = null
                this.searchTerm = null
                this.isSearchResult = false
                
                try {
                    let urlBase = `/api/standardphrases/?paged=true`
                    let pageUrl =`${urlBase}&pageSize=${pageSize}&page=${pageNumber}`
                    let response = await axios.get(pageUrl)
                    let resultsPage = response.data

                    this.pageNumber = resultsPage.page_number
                    this.totalPages = resultsPage.total_pages
                    this.totalResults = resultsPage.total_results
                    this.pageSize = resultsPage.page_size
                    
                    // Serialize tagged categories to string
                    resultsPage.results.forEach((r) => {
                        r.categories_str = r.categories ? r.categories.join(', ') : ''
                        r.name = r.name && r.name !== 'NULL' ? r.name : ''
                    })

                    this.phrases = resultsPage.results                    
                    this.isSearchResult = false
                    this.searchTermWas = null
                    this.viewState = 'Idle'
                    
                } catch (err) {
                    console.error('Error loading phrases', err)
                    this.viewState = 'Error'
                    this.error = err
                }
            },
            async searchPhrases() {
                if (!this.searchTerm) {
                    await this.loadPhrases(this.resultPageSize, this.resultPageNumber)
                    return
                }

                this.viewState = 'Busy'
                try {
                    let searchUrl = `/api/standardphrases/?searchTerm=${this.searchTerm}`
                    let response = await axios.get(searchUrl)

                    this.searchTermWas = this.searchTerm
                    this.isSearchResult = true

                    response.data.forEach((r) => {
                        r.categories_str = r.categories ? r.categories.join(', ') : ''
                        r.name = r.name && r.name !== 'NULL' ? r.name : ''
                    })

                    this.phrases = response.data
                    this.viewState = 'Idle'
                } catch (err) {
                    console.error('Error loading search results', err)
                    this.viewState = 'Error'
                    this.error = err
                }
            }
        }
    }
</script>